import React, { Fragment } from 'react';

import {
  Alert,
  LegacyButton,
  Margin,
  Spacer,
  Spinner,
} from '@zapier/design-system';

import { usePublishingForm } from 'app/developer-v3/hooks';

import { useSidebarRight } from '../Provider';
import {
  MODES as SIDEBAR_RIGHT_MODES,
  TABS as SIDEBAR_RIGHT_TABS,
} from '../constants';

import type { SemVer } from 'app/developer-v3/types/version';

type Props = {
  canAdvance: boolean;
  id: string;
  isSummaryActive: boolean;
  mode: SIDEBAR_RIGHT_MODES;
  version: SemVer;
};

function ActivationMessage({
  canAdvance,
  id,
  isSummaryActive,
  mode,
  version,
}: Props) {
  const {
    isWorking,
    publishingForm,
    submit: submitPublishingForm,
  } = usePublishingForm({ integrationId: Number(id) });
  const { update: updateSidebarRight } = useSidebarRight();

  const isPublishing = mode === SIDEBAR_RIGHT_MODES.PUBLISHING;
  const title = `Before you ${isPublishing ? 'publish' : 'promote'}`;
  const message = isPublishing
    ? canAdvance
      ? 'Please review warning messages and address the items described if possible. Warnings will need to be reviewed by Zapier and will delay publishing.'
      : 'Please address these errors and tasks. When complete, re-validate your integration and publish again.'
    : canAdvance
    ? 'Please be sure to review all warnings and address them if possible.'
    : 'Please address all errors and tasks. When you have finished, re-validate your integration and promote again.';
  const onClickCta = () => {
    if (isPublishing || !isSummaryActive) {
      submitPublishingForm({ publishingForm, version });
      updateSidebarRight({
        mode: SIDEBAR_RIGHT_MODES.DEFAULT,
        tabActive: SIDEBAR_RIGHT_TABS.NONE,
      });
    }
  };
  const buttonLabel = isPublishing
    ? 'Continue publishing'
    : isSummaryActive
    ? 'See warnings'
    : 'Promote with warnings';

  return (
    <aside>
      <Margin margin="15px">
        {isWorking ? (
          <Spinner />
        ) : (
          <Alert status="warning" title={title}>
            {message}
            {canAdvance && (
              <Fragment>
                <Spacer height={25} />
                <LegacyButton
                  color="secondary"
                  iconBefore="arrowBigRight"
                  onClick={onClickCta}
                >
                  {buttonLabel}
                </LegacyButton>
              </Fragment>
            )}
          </Alert>
        )}
      </Margin>
    </aside>
  );
}

ActivationMessage.defaultProps = {
  canAdvance: false,
  isSummaryActive: false,
};

export default ActivationMessage;
